import React, { FC, useCallback, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import { ViewportMenu } from 'ui';
import { findStyleMasterVariants } from '../../garments';
import { setStyleVariant } from '../actions/homeGarment';
import { HomeState } from '../store/homeState';
import { homeStateExt } from '../store/homeStateExt';

type Item = {
  id: string;
  preview: string;
  variant: string;
};

/**
 * HomeVariants custom hook.
 */
const useVariants = () => {
  const { dispatch, getState } = useStore<HomeState>();

  const ctxStyle = useSelector((state: HomeState) => state.style);

  const [variant, variants] = useMemo(() => {
    // No style
    if (!ctxStyle) {
      return [undefined, []];
    }

    // Master variants from style
    const style = homeStateExt.style!;
    const variants: Item[] = findStyleMasterVariants(style).map(g => ({
      id: g.id,
      preview: g.preview,
      variant: g.variant,
    }));

    return [ctxStyle.variant, variants];
  }, [ctxStyle]);

  return { dispatch, getState, variant, variants };
};

/**
 * Variants viewport menu.
 */
export const HomeVariants: FC = () => {
  const { dispatch, getState, variant, variants } = useVariants();

  const items = useMemo(
    () =>
      (variants ?? []).map(v => {
        return {
          icon: v.preview,
          key: v.variant,
        };
      }),
    [variants],
  );

  const onChange = useCallback(
    (variant: string) => {
      setStyleVariant(dispatch, getState, variant);
    },
    [dispatch, getState],
  );

  return <ViewportMenu items={items} onChange={onChange} selection={variant} />;
};
